<script setup lang="ts">
import { TabsTrigger } from 'reka-ui'
import { computed } from 'vue'

import ARouterLink from '../ARouterLink/ARouterLink.vue'
import { ASkeleton } from '../skeleton'
import type { ATabItem, ATabsProperties } from '.'
import { Tabs, TabsContent, TabsList } from '.'

const tab = defineModel<string | undefined>({ default: undefined, required: false })

const properties = withDefaults(defineProps<ATabsProperties>(), {
  items: () => [],
  skeleton: false,
})

const tabs = computed<ATabItem[]>(() => {
  return properties.items.map((item, index) => {
    return {
      ...item,
      value: `${item.value || item.to?.name || index}`,
    }
  })
})
</script>

<template>
  <Tabs v-model="tab" class="flex w-full flex-col gap-6" :class="properties.class" :data-test :default-value="tab">
    <div class="flex w-full">
      <ASkeleton v-if="properties.skeleton" class="h-[47px] w-full" />
      <TabsList v-else class="flex w-full flex-row">
        <template v-for="item in tabs" :key="item.value">
          <ARouterLink v-if="item.to" class="atabix-tab-trigger" :data-test="item.dataTest" :to="item.to" :value="String(item.value)">
            {{ item.title }}
          </ARouterLink>
          <TabsTrigger v-if="!item.to" class="atabix-tab-trigger" :value="String(item.value)">
            {{ item.title }}
          </TabsTrigger>
        </template>
      </TabsList>
    </div>
    <ASkeleton v-if="properties.skeleton" class="h-96" />
    <slot v-else>
      <template v-for="item in tabs" :key="item.value">
        <TabsContent v-if="item.component" :data-test="item.dataTest" :value="String(item.value)">
          <component :is="item.component" />
        </TabsContent>
      </template>
    </slot>
  </Tabs>
</template>

<style lang="scss">
.atabix-tab-trigger {
  @apply select-none;
  @apply inline-flex;
  @apply flex-grow;
  @apply items-center;
  @apply justify-center;
  @apply hover:bg-background-selected;
  @apply hover:text-foreground-accent;
  @apply whitespace-nowrap;
  @apply rounded-sm;
  @apply px-3;
  @apply py-1.5;
  @apply text-base;
  @apply font-medium;
  @apply ring-offset-background;
  @apply transition-all;
  @apply focus-visible:outline-none;
  @apply focus-visible:ring-2;
  @apply focus-visible:ring-offset-2;
  @apply disabled:pointer-events-none;
  @apply disabled:opacity-50;
  @apply data-[state=active]:shadow-sm;
}

.atabix-tab-trigger.router-link-exact-active,
.atabix-tab-trigger[data-state='active'] {
  @apply bg-background text-foreground;
}
</style>
